import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ExpenseModel } from '../model/expense.model';
import { ExpenseService } from '../expense.service';
import { SnackBarData } from 'src/app/common/components/snackbar/model/snackbar.model';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/common/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DirtyCheck } from 'src/app/common/guards/form-deactivate.guard';
import { PermissionService } from 'src/app/auth/permission.service';

@Component({
  selector: 'app-expense-edit',
  templateUrl: './expense-edit.component.html',
  styleUrls: ['./expense-edit.component.scss'],
  standalone: false
})
export class ExpenseEditComponent implements DirtyCheck {
  title: string = "New Expense";
  editForm!: FormGroup;
  expenseInfo: ExpenseModel = {};
  isFormDirty: boolean = false;
  ratingList: ExpenseModel[] = [];
  expenseType: any[] = [];
  filteredExpenseTypes: any[] = [];
  permission: any;

  constructor(
    private router: Router,
    private builder: FormBuilder,
    private route: ActivatedRoute,
    private expenseService: ExpenseService,
    private snackBarService: SnackBarService,
    private permissionService: PermissionService,
    public dialog: MatDialog,
  ) {
    let expenseId = this.route.snapshot.paramMap.get('id');
    if (expenseId !== null && parseInt(expenseId) !== 0) {
      this.setExpenseInfo(expenseId);
    } else {
      this.initForm();
    }
    this.getExpenseType();

    this.permission = permissionService.getPagePermission("expense");

  }

  ngOnInit(): void { }

  getExpenseType() {
    let requestData = {
      "search": {
        "filters": {
          "simpleSearch": [],
          "advSearch": [],
          "scope": {
            "limit": 10,
            "offset": 0
          }
        },
        "sort": [
          {
            "column": "name",
            "order": "asc"
          }
        ]
      }
    }
    this.expenseService.getExpenseType(requestData).subscribe({
      next: (response) => {
        if (response.status == 'SUCCESS') {
          this.expenseType = response.data.expenses_type;
          this.filteredExpenseTypes = this.expenseType.slice();
          this.initForm();
        }
      },
      error: (error) => {
        let snackBarData: SnackBarData = {
          message: 'Failed to get expense type from server. Connection to the server failed.',
          title: 'Failed!!!',
          type: 'error'
        }
        this.snackBarService.openSnackBar(snackBarData);
      }
    });
  }

  initForm() {
    this.editForm = this.builder.group({
      code: this.builder.control(this.expenseInfo.code, [Validators.required, Validators.maxLength(10)]),
      name: this.builder.control(this.expenseInfo.name, [Validators.required, Validators.maxLength(50)]),
      description: this.builder.control(this.expenseInfo.description),
      expense_type: this.builder.control(
        this.getExpenseTypeObject(this.expenseInfo.expense_type),
        [this.expenseTypeValidator.bind(this)]
      )
    });

    this.editForm.valueChanges.subscribe(() => {
      this.isFormDirty = true;
    });
  }

  getExpenseTypeObject(id: any) {
    if (!id) return '';
    return this.expenseType?.find(et => et.id == id) || '';
  }

  filterExpenseTypes() {
    const value = this.editForm.get('expense_type')?.value;
    const filterValue = typeof value === 'string' ? value.toLowerCase() : (value?.name?.toLowerCase() || '');
    this.filteredExpenseTypes = this.expenseType.filter(et =>
      et.name.toLowerCase().includes(filterValue)
    );
  }

  displayExpenseType(et: any): string {
    return et && et.name ? et.name : '';
  }

  clearExpenseType() {
    this.editForm.get('expense_type')?.setValue('');
    this.filteredExpenseTypes = this.expenseType.slice();
  }

  expenseTypeValidator(control: AbstractControl) {
    if (!control.value) return null;
    if (typeof control.value === 'object' && this.expenseType.some(et => et.id === control.value.id)) {
      return null;
    }
    if (typeof control.value === 'string' && this.expenseType.some(et => et.name === control.value)) {
      return null;
    }
    return { invalidExpenseType: true };
  }

  /**
   * Dirtyness check
   * @returns 
   */
  isDirty(): boolean {
    return this.isFormDirty;
  }

  /**
   * checks the form is edited
   */
  navBack(): void {

    this.router.navigate(['/system/expense']);

  }

  /**
   * If tas is is null set an empty expense info
   * if expense id is not null call api to rtrive the info
   * @param expenseId 
   */
  setExpenseInfo(expenseId: any): void {

    let response = this.expenseService.getExpenseById(expenseId).subscribe({
      next: (response) => {
        if (response.status == 'SUCCESS') {
          this.expenseInfo = response.data.expense;
          this.editForm.patchValue({
            ...this.expenseInfo,
            expense_type: this.getExpenseTypeObject(this.expenseInfo.expense_type)
          });
          this.title = this.getTitle();
          this.isFormDirty = false;
        } else {
          let snackBarData: SnackBarData = {
            message: response.message,
            title: 'Failed!!!',
            type: 'error'
          }
          this.snackBarService.openSnackBar(snackBarData);
        }
      },
      error: (error) => {
        let snackBarData: SnackBarData = {
          message: 'Failed to get data from server. Connection to the server failed.',
          title: 'Failed!!!',
          type: 'error'
        }
        this.snackBarService.openSnackBar(snackBarData);
      }
    });
  }

  /**
   * 
   * @returns  the title for the dialog
   * sine the same dialog is used for edit and new, dynamically decide the title
   */
  getTitle(): string {
    let title = (this.expenseInfo.id == 0 || this.expenseInfo.id == undefined) ? "New Expense" : "Editing : " + this.expenseInfo.name;
    return title;
  }

  /**
     * On save
     * if the details are valid call the api to update the changes.
     */
  save(): void {

    if (this.editForm.valid) {
      this.editForm.markAllAsTouched();
      Object.assign(this.expenseInfo, this.editForm.value);

      // Only send the expense_type id to the backend
      let expense_type = this.editForm.value.expense_type;
      if (expense_type && typeof expense_type === 'object') {
        expense_type = expense_type.id;
      }
      this.expenseInfo.expense_type = expense_type;

      this.expenseService.update(this.expenseInfo).subscribe({
        next: (response) => {
          if (response.status == 'SUCCESS') {
            this.isFormDirty = false;
            let snckBarData: SnackBarData = {
              type: 'success',
              title: 'Saved',
              message: 'The record for ' + this.expenseInfo.name + " has been updated succesfully."
            }
            this.snackBarService.openSnackBar(snckBarData);
            this.router.navigate(['/system/expense']);
          } else {
            let snackBarData: SnackBarData = {
              message: response.message,
              title: 'Failed!!!',
              type: 'error'
            }
            this.snackBarService.openSnackBar(snackBarData);
          }
        },
        error: (error) => {
          let snackBarData: SnackBarData = {
            message: 'Failed to update. Connection to the server failed',
            title: 'Failed!!!',
            type: 'error'
          }
          this.snackBarService.openSnackBar(snackBarData);
        }
      });

    } else {
      this.editForm.markAllAsTouched();
    }
  }
}
